import ReactFlow, {
  Background,
  Controls,
  type Edge,
  type NodeChange,
  type NodeDragHandler,
} from "reactflow";
import "reactflow/dist/style.css";
import { EditableEdgeProvider } from "@/features/lines/canvas/edges/EditableEdgeContext";
import type { SandboxRfNode } from "./hooks/useSandboxFlow";
import { sandboxNodeTypes, sandboxEdgeTypes } from "./SandboxMachineNode";

// Superfície React Flow do sandbox por estágios. Reusa nós/edges do editor de linha (START/END/
// PRODUCTION_FLOW). Edges são DERIVADAS de estágios ⇒ não editáveis à mão nem conectáveis
// (EditableEdge desligado, `nodesConnectable=false`; os Handles dos nós de máquina continuam no
// DOM só como âncora de edge — sem eles o React Flow não renderiza nenhuma linha). A estrutura
// muda arrastando a máquina pra outra lane (onNodeDragStop, hit-test em lane-helpers.ts) — a
// posição nunca é persistida no store, mas o React Flow SÓ segue o cursor em tempo real se
// `onNodesChange` estiver plugado (nodes controlados por prop) — daí ele existir aqui mesmo sem
// os nodes serem "editáveis à mão" (o hook usa só pra um override efêmero de posição durante o
// drag, ver useSandboxFlow.ts).
interface Props {
  nodes: SandboxRfNode[];
  edges: Edge[];
  editable: boolean;
  onNodeClick?: (key: string) => void;
  onNodesChange?: (changes: NodeChange[]) => void;
  onNodeDragStop?: NodeDragHandler;
  onPaneClick?: () => void;
}

export function SandboxFlowSurface({
  nodes,
  edges,
  editable,
  onNodeClick,
  onNodesChange,
  onNodeDragStop,
  onPaneClick,
}: Props) {
  return (
    <EditableEdgeProvider value={{ isEdit: false, onDeleteEdge: () => {} }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={sandboxNodeTypes}
        edgeTypes={sandboxEdgeTypes}
        nodesDraggable={editable}
        nodesConnectable={false}
        elementsSelectable
        // Sandbox não tem noção de "mover várias máquinas juntas" — desliga multi-seleção (click
        // com modificador + rubber-band) pra nunca cair no caso de arrastar 2+ nodes ao mesmo
        // tempo (onNodeDragStop só resolve o node âncora, não os outros da seleção).
        multiSelectionKeyCode={null}
        selectionKeyCode={null}
        panOnDrag
        zoomOnScroll
        fitView
        fitViewOptions={{ padding: 0.2, maxZoom: 1.4 }}
        // STAGE_LANE é fundo não-selecionável (selectable:false só afasta o estilo de seleção,
        // não bloqueia o click). O clique borbulha de QUALQUER filho (Input/botões do header
        // incluídos, que não dão stopPropagation) — só reage quando é de fato um clique em
        // MACHINE; clique em lane (fundo ou controle do header) não deve mexer em selectedKey.
        onNodeClick={(_, node) => {
          if (node.type === "MACHINE") onNodeClick?.(node.id);
        }}
        onNodesChange={onNodesChange}
        onNodeDragStop={onNodeDragStop}
        onPaneClick={() => onPaneClick?.()}
        proOptions={{ hideAttribution: true }}
      >
        <Background gap={16} size={1} />
        <Controls showInteractive={false} />
      </ReactFlow>
    </EditableEdgeProvider>
  );
}
